import { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Play } from 'lucide-react';
import { useAnalytics } from '@/hooks/useAnalytics';

interface YouTubeEmbedProps {
  /** ID do vídeo no YouTube (ex: DdEBCi7EH8s) */
  videoId: string;
  /** Título acessível do vídeo */
  title: string;
  /** Título para SEO/schema (opcional, usa title como fallback) */
  seoTitle?: string;
  /** Descrição para SEO/schema */
  description?: string;
  /** Data de publicação (ISO 8601) para schema */
  publishedAt?: string;
  /** Duração em segundos para schema (ex: 45) */
  durationSeconds?: number;
  /** Se é um Shorts (ratio 9:16) ou vídeo normal (16:9) */
  variant?: 'video' | 'shorts';
  /** Classe extra */
  className?: string;
  /** Carregar automaticamente (sem clique no poster) */
  autoLoad?: boolean;
}

function getYouTubeThumbnail(videoId: string): string {
  return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
}

function getYouTubeEmbedUrl(videoId: string): string {
  return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `PT${m}M${s}S`;
}

export function YouTubeEmbed({
  videoId,
  title,
  seoTitle,
  description = '',
  publishedAt,
  durationSeconds,
  variant = 'video',
  className = '',
  autoLoad = false,
}: YouTubeEmbedProps) {
  const [loaded, setLoaded] = useState(autoLoad);
  const [inView, setInView] = useState(autoLoad);
  const containerRef = useRef<HTMLDivElement>(null);
  const { trackVideo } = useAnalytics();

  // Lazy load: só observa quando o container entra no viewport
  useEffect(() => {
    if (autoLoad) return;
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [autoLoad]);

  const ratioClass = variant === 'shorts' ? 'aspect-[9/16] max-w-[360px] mx-auto' : 'aspect-video';

  const thumbnailUrl = getYouTubeThumbnail(videoId);
  const embedUrl = getYouTubeEmbedUrl(videoId);
  const pageUrl = `https://www.youtube.com/watch?v=${videoId}`;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: seoTitle || title,
    description,
    thumbnailUrl,
    uploadDate: publishedAt || new Date().toISOString(),
    contentUrl: pageUrl,
    embedUrl,
    ...(durationSeconds ? { duration: formatDuration(durationSeconds) } : {}),
    publisher: {
      '@type': 'Organization',
      name: 'vemprailhadomel.com.br',
      logo: {
        '@type': 'ImageObject',
        url: 'https://vemprailhadomel.com.br/favicon.ico',
      },
    },
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <div ref={containerRef} className={`my-8 ${className}`}>
        <div
          className={`relative w-full ${ratioClass} rounded-2xl overflow-hidden bg-sand-100 border border-sand-200 shadow-[0_2px_12px_rgba(0,0,0,0.08)]`}
        >
          {!loaded ? (
            <button
              onClick={() => {
                trackVideo(title, 'YouTube', pageUrl);
                setLoaded(true);
              }}
              className="absolute inset-0 w-full h-full group cursor-pointer"
              aria-label={`Assistir: ${title}`}
            >
              {inView && (
                <img
                  src={thumbnailUrl}
                  alt={title}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  onError={e => {
                    // Fallback para thumbnail padrão se maxres não existir
                    (e.target as HTMLImageElement).src = thumbnailUrl.replace(
                      'maxresdefault',
                      'hqdefault'
                    );
                  }}
                />
              )}
              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-coral rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <Play className="w-7 h-7 sm:w-9 sm:h-9 text-white fill-white ml-1" />
                </div>
              </div>
            </button>
          ) : (
            <iframe
              src={embedUrl}
              title={title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              loading="lazy"
              className="absolute inset-0 w-full h-full"
            />
          )}
        </div>
        <p className="font-body text-xs text-sand-400 mt-2 text-center">🎬 {title}</p>
      </div>
    </>
  );
}
